/**
 * StockTransferService — atomic writer for inter-godown stock transfers.
 *
 * Each call to `createStockTransfer` performs a single database.write()
 * that:
 *   1. Reserves a transfer number (`TR-<base36 ts>-<rand>`)
 *   2. Creates a StockTransfer row in status=COMPLETED
 *   3. Creates one StockTransferItem per product
 *   4. Emits two StockMovement rows per item (OUT from source, IN to dest)
 *
 * Per-product `currentStock` does NOT change — the source/destination
 * balances live in StockMovement rows scoped to the godown. This matches
 * mobile semantics: Product.currentStock is the merchant-wide on-hand,
 * and individual godowns are just sub-buckets.
 */

import { database, txn } from '@/db';
import { StockTransfer, StockTransferItem, StockMovement, Product } from '@/db/models/Inventory';
import { prepareAuditLog } from '@/utils/auditLog';

export interface TransferLine {
  product: Product;
  quantity: number;
  batchId?: string | null;
}

export interface CreateTransferInput {
  fromGodownId: string;
  toGodownId: string;
  lines: TransferLine[];
  notes?: string | null;
  createdById: string;
}

export interface CreateTransferResult {
  success: boolean;
  transferId?: string;
  transferNumber?: string;
  error?: string;
}

function newTransferNumber(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `TR-${ts}-${rand}`;
}

export async function createStockTransfer(
  input: CreateTransferInput,
): Promise<CreateTransferResult> {
  if (input.fromGodownId === input.toGodownId) {
    return { success: false, error: 'Source and destination godowns must differ.' };
  }
  const cleanLines = input.lines.filter((l) => l.quantity > 0);
  if (cleanLines.length === 0) {
    return { success: false, error: 'Add at least one line with quantity > 0.' };
  }

  const transferNumber = newTransferNumber();
  let transferId = '';

  try {
    await txn(async (db) => {
      const ops: any[] = [];

      const transfer = db.get<StockTransfer>('stock_transfers').prepareCreate((t: StockTransfer) => {
        t.transferNumber = transferNumber;
        t.fromGodownId = input.fromGodownId;
        t.toGodownId = input.toGodownId;
        t.status = 'COMPLETED';
        t.notes = input.notes ?? null;
        t.dispatchedAt = new Date();
        t.receivedAt = new Date();
        t.createdById = input.createdById;
        t.isSynced = false;
        t.version = 1;
        t.isDeleted = false;
      });
      transferId = (transfer as StockTransfer).id;
      ops.push(transfer);

      for (const line of cleanLines) {
        ops.push(
          db.get<StockTransferItem>('stock_transfer_items').prepareCreate((sti: StockTransferItem) => {
            sti.stockTransferId = transferId;
            sti.productId = line.product.id;
            sti.batchId = line.batchId ?? null;
            sti.quantity = line.quantity;
            sti.isSynced = false;
            sti.version = 1;
            sti.isDeleted = false;
          }),
        );

        // OUT from source
        ops.push(
          db.get<StockMovement>('stock_movements').prepareCreate((sm: StockMovement) => {
            sm.productId = line.product.id;
            sm.godownId = input.fromGodownId;
            sm.type = 'TRANSFER_OUT';
            sm.quantity = -line.quantity;
            sm.referenceId = transferId;
            sm.referenceType = 'STOCK_TRANSFER';
            sm.notes = `Transfer ${transferNumber}`;
            sm.source = 'DESKTOP';
            sm.userId = input.createdById;
            sm.isSynced = false;
            sm.version = 1;
            sm.isDeleted = false;
          }),
        );

        // IN to destination
        ops.push(
          db.get<StockMovement>('stock_movements').prepareCreate((sm: StockMovement) => {
            sm.productId = line.product.id;
            sm.godownId = input.toGodownId;
            sm.type = 'TRANSFER_IN';
            sm.quantity = line.quantity;
            sm.referenceId = transferId;
            sm.referenceType = 'STOCK_TRANSFER';
            sm.notes = `Transfer ${transferNumber}`;
            sm.source = 'DESKTOP';
            sm.userId = input.createdById;
            sm.isSynced = false;
            sm.version = 1;
            sm.isDeleted = false;
          }),
        );
      }

      const totalQty = cleanLines.reduce((sum, l) => sum + l.quantity, 0);
      ops.push(prepareAuditLog({
        action: 'stock_transferred',
        entityType: 'stock_transfer',
        entityId: transferId,
        userId: input.createdById,
        changes: {
          summary: `${transferNumber} · ${cleanLines.length} product${cleanLines.length === 1 ? '' : 's'} · ${totalQty} units moved`,
          quantity: totalQty,
          ref: transferNumber,
          after: { fromGodownId: input.fromGodownId, toGodownId: input.toGodownId },
        },
      }));

      await db.batch(...ops);
    });
    return { success: true, transferId, transferNumber };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
}
